/**
 * PatientInvoicesBlock Component
 *
 * Collapsible panel for viewing patient invoices through Stripe Connect.
 * Only visible when the provider has Stripe Connect enabled.
 */

import { useCallback, useState } from 'react';
import CollapsibleBaseBlock from '@components/Patient/Blocks/CollapsibleBaseBlock';
import { Receipt as ReceiptIcon } from '@mui/icons-material';
import { Alert, Box, Chip, CircularProgress, Stack } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { InvoiceResponse } from '@store/generated/payments';

import { useLazyGetInvoiceUrlsQuery } from '../store/api';
import type { InvoiceStatusFilter } from '../store/hooks';
import { usePatientInvoices } from '../store/hooks';

import { PatientInvoicesList } from './PatientInvoicesList';

const STATUS_FILTERS: { value: InvoiceStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'paid', label: 'Paid' },
];

export interface PatientInvoicesBlockProps {
  patientId: string;
  defaultCollapsed?: boolean;
  /** When true, shows the block in preview mode even if Stripe is not enabled */
  previewMode?: boolean;
}

/**
 * Main collapsible block for patient invoices
 * Only renders when Stripe Connect is enabled for the provider
 */
export function PatientInvoicesBlock({
  patientId,
  defaultCollapsed = true,
  previewMode = false,
}: PatientInvoicesBlockProps) {
  const { patientInvoice } = useEventTracking();

  const [hasLoadedData, setHasLoadedData] = useState(!defaultCollapsed);
  const [statusFilter, setStatusFilter] = useState<InvoiceStatusFilter>('all');
  const [fetchInvoiceUrls] = useLazyGetInvoiceUrlsQuery();
  const [loadingInvoice, setLoadingInvoice] = useState<{
    id: string;
    action: 'view' | 'download';
  } | null>(null);

  const {
    invoices,
    isStripeEnabled,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    refetch,
  } = usePatientInvoices(patientId, {
    skip: defaultCollapsed && !hasLoadedData,
    status: statusFilter,
  });

  // Track panel expand
  const handleCollapseChange = useCallback(
    (isCollapsed: boolean) => {
      if (!isCollapsed && !hasLoadedData) {
        setHasLoadedData(true);
        refetch?.();
      }

      if (!isCollapsed) {
        patientInvoice?.panelExpanded(patientId, {
          invoices_count: invoices.length,
        });
      }
    },
    [hasLoadedData, patientId, invoices.length, patientInvoice, refetch],
  );

  // Fetch fresh URLs from Stripe on demand (stored URLs expire)
  const fetchAndOpenUrl = useCallback(
    async (invoice: InvoiceResponse, urlKey: 'payment_url' | 'pdf_url') => {
      const action = urlKey === 'payment_url' ? 'view' : 'download';
      setLoadingInvoice({ id: invoice.id, action });
      try {
        const result = await fetchInvoiceUrls({
          patientId,
          invoiceId: invoice.id,
        }).unwrap();
        const url = result[urlKey];
        if (url) {
          if (action === 'view') {
            patientInvoice?.invoiceViewed(patientId, invoice.id, {
              invoice_status: invoice.status,
            });
          } else {
            patientInvoice?.invoiceDownloaded(patientId, invoice.id, {
              invoice_status: invoice.status,
            });
          }
          window.open(url, '_blank');
        }
      } catch {
        // URL fetch failed — Stripe account may not be configured
      } finally {
        setLoadingInvoice(null);
      }
    },
    [patientId, fetchInvoiceUrls, patientInvoice],
  );

  // Handle view invoice
  const handleView = useCallback(
    (invoice: InvoiceResponse) => {
      fetchAndOpenUrl(invoice, 'payment_url');
    },
    [fetchAndOpenUrl],
  );

  // Handle download invoice PDF
  const handleDownload = useCallback(
    (invoice: InvoiceResponse) => {
      fetchAndOpenUrl(invoice, 'pdf_url');
    },
    [fetchAndOpenUrl],
  );

  // Don't render if Stripe Connect is not enabled (unless in preview mode)
  if (!isStripeEnabled && !previewMode) {
    return null;
  }

  // In preview mode without Stripe enabled, show a preview state
  const isPreviewState = previewMode && !isStripeEnabled;

  return (
    <CollapsibleBaseBlock
      defaultCollapsed={defaultCollapsed}
      onCollapseChange={handleCollapseChange}
    >
      <CollapsibleBaseBlock.Header
        title="Invoices"
        icon={<ReceiptIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
        loading={isLoading && !isPreviewState}
      />

      <CollapsibleBaseBlock.Body>
        {isPreviewState ? (
          <Box
            sx={{
              py: 3,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            No invoices yet
          </Box>
        ) : (
          <>
            {/* Status filter chips */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {STATUS_FILTERS.map(filter => (
                <Chip
                  key={filter.value}
                  label={filter.label}
                  size="small"
                  variant={statusFilter === filter.value ? 'filled' : 'outlined'}
                  color={statusFilter === filter.value ? 'primary' : 'default'}
                  onClick={() => setStatusFilter(filter.value)}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: statusFilter === filter.value ? 500 : 400,
                  }}
                />
              ))}
            </Stack>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load invoices. Please try again.
              </Alert>
            ) : (
              <PatientInvoicesList
                invoices={invoices}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                onView={handleView}
                onDownload={handleDownload}
                onLoadMore={loadMore}
                loadingInvoice={loadingInvoice}
              />
            )}
          </>
        )}
      </CollapsibleBaseBlock.Body>
    </CollapsibleBaseBlock>
  );
}

export default PatientInvoicesBlock;
